import { Box, Grid, styled, Typography } from '@mui/material';
import { snakify } from 'helpers/syntaxHelper';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HrCard from 'routes/HumanResourcesModule/components/HrCard';
import { getPositionValue } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/gridHelpers';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { ApplyChangesButton } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/ApplyChangesButton';
import {
    DEFAULT_HYPOTHESIS_TYPE_PER_STATUS,
    NonRecurrentHypothesisType,
} from './helpers/constants';
import { findMismatchedKeys } from './helpers/helpers';
import { NonRecurrentHypothesisTypePerMantyStatus } from './helpers/types';
import {
    useGetNonRecurrentHypothesisTypePerMantyStatus,
    usePatchNonRecurrentHypothesisTypePerMantyStatus,
} from './helpers/hooks/nonRecurrentHypothesisTypePerMantyStatus';
import HypothesisTypeItem from './HypothesisTypeItem';
import { FilteredMantyStatus } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Arrival/helpers/constants';

type Props = { hrSimulation: HrSimulation };

const StyHrCard = styled(HrCard)(() => ({
    '.MuiCardHeader-action': { mr: 0 },
}));

const StyBox = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.primary.background,
    height: '100%',
    borderRadius: '0.5rem',
}));

const HypothesisTypePerMantyStatus = ({
    hrSimulation,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    // By default the statuses 'Elu' and 'Titulaire' are not displayed in the non recurrent tab

    const [values, setValues] =
        useState<NonRecurrentHypothesisTypePerMantyStatus>(
            DEFAULT_HYPOTHESIS_TYPE_PER_STATUS,
        );

    const handleChange = useCallback(
        (key: string, newValue: NonRecurrentHypothesisType) => {
            setValues((prev) => ({ ...prev, [key]: newValue }));
        },
        [],
    );

    const {
        data: hypothesisTypePerMantyStatus,
        isLoading: isHypothesisTypePerMantyStatusLoading,
    } = useGetNonRecurrentHypothesisTypePerMantyStatus(hrSimulation.id);

    const {
        mutate: updateHypothesesType,
        isPending: isUpdateHypothesesTypeLoading,
    } = usePatchNonRecurrentHypothesisTypePerMantyStatus(hrSimulation.id);

    useEffect(() => {
        if (hypothesisTypePerMantyStatus) {
            setValues(hypothesisTypePerMantyStatus);
        }
    }, [hypothesisTypePerMantyStatus]);

    const mismatchedKeys =
        hypothesisTypePerMantyStatus === undefined ||
        isHypothesisTypePerMantyStatusLoading
            ? []
            : findMismatchedKeys({
                  oldParams: hypothesisTypePerMantyStatus,
                  newParams: values,
              });

    const onValidate = useCallback(() => {
        updateHypothesesType(values);
    }, [values, updateHypothesesType]);

    return (
        <StyHrCard
            title={t('humanResources.simulation.manageHypothesisTypePerStatus')}
            cardFooterActions={
                <Grid container justifyContent="flex-end" alignItems="center">
                    <Grid item>
                        {mismatchedKeys.length > 0 && (
                            <Typography
                                variant="body2"
                                color="error"
                                sx={{ mr: '16px' }}
                            >
                                {t(
                                    'humanResources.simulation.warningHypothesisDelete',
                                    {
                                        status: mismatchedKeys
                                            .map((mk) =>
                                                getPositionValue(snakify(mk)),
                                            )
                                            .join(', '),
                                        count: mismatchedKeys.length,
                                    },
                                )}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item>
                        <ApplyChangesButton
                            isLoading={
                                isHypothesisTypePerMantyStatusLoading ||
                                isUpdateHypothesesTypeLoading
                            }
                            isDisabled={mismatchedKeys.length === 0}
                            onClick={onValidate}
                            title={t(
                                'humanResources.simulation.validateChanges',
                            )}
                        />
                    </Grid>
                </Grid>
            }
        >
            <StyBox sx={{ width: '100%' }}>
                <Grid container columnSpacing={{ xs: 4, m: 14 }}>
                    {FilteredMantyStatus.map((status) => (
                        <HypothesisTypeItem
                            key={status}
                            status={status}
                            isLoading={isHypothesisTypePerMantyStatusLoading}
                            values={values}
                            handleChange={handleChange}
                        />
                    ))}
                </Grid>
            </StyBox>
        </StyHrCard>
    );
};

export default HypothesisTypePerMantyStatus;
